import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, setDoc, onSnapshot } from 'firebase/firestore';
import { updatePassword, EmailAuthProvider, reauthenticateWithCredential, deleteUser } from 'firebase/auth';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { useDarkMode } from '../contexts/DarkModeContext';
import { requestNotificationPermission, disableNotifications } from '../utils/notifications';
import TeamAuswahl from '../components/TeamAuswahl';
import { NFL_TEAMS } from '../data/nflTeams';
import './Einstellungen.css';

// Einstellungen: Push, Dark Mode, Profil (Name, Lieblingsteam), Passwort,
// Account löschen. Schreibt nur das eigene users-Doc (Felder unverändert:
// displayName, favoritenTeam, favoritenTeamName, notificationsEnabled,
// fcmToken; beim Löschen deleted/deletedAt/email).

function Einstellungen() {
  const { currentUser, userProfile } = useAuth();
  const { darkMode, toggleDarkMode } = useDarkMode();
  const navigate = useNavigate();
  const uid = currentUser?.uid;

  const [displayName, setDisplayName] = useState('');
  const [favoritenTeam, setFavoritenTeam] = useState('');
  const [gespeichertesProfil, setGespeichertesProfil] = useState({ displayName: '', favoritenTeam: '' });
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const [busy, setBusy] = useState(null); // 'push' | 'profil' | 'passwort' | 'loeschen'

  const [altesPasswort, setAltesPasswort] = useState('');
  const [neuesPasswort, setNeuesPasswort] = useState('');
  const [bestaetigung, setBestaetigung] = useState('');
  const [zeigePasswort, setZeigePasswort] = useState(false);

  const [loeschenOffen, setLoeschenOffen] = useState(false);
  const [loeschenPasswort, setLoeschenPasswort] = useState('');

  const [toast, setToast] = useState(null);
  const toastTimer = useRef(null);
  const zeigeToast = useCallback((text, fehler = false) => {
    setToast({ text, fehler });
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 3000);
  }, []);

  const pushMoeglich = 'Notification' in window && 'serviceWorker' in navigator;

  useEffect(() => {
    if (!uid) return undefined;
    const unsub = onSnapshot(doc(db, 'users', uid), snap => {
      if (!snap.exists()) return;
      const d = snap.data();
      setDisplayName(d.displayName || '');
      setFavoritenTeam(d.favoritenTeam || '');
      setGespeichertesProfil({ displayName: d.displayName || '', favoritenTeam: d.favoritenTeam || '' });
      setNotificationsEnabled(d.notificationsEnabled === true);
    });
    return () => unsub();
  }, [uid]);

  const profilGeaendert = displayName.trim() !== gespeichertesProfil.displayName || favoritenTeam !== gespeichertesProfil.favoritenTeam;

  const handlePush = async () => {
    if (busy) return;
    setBusy('push');
    try {
      if (notificationsEnabled) {
        await disableNotifications(uid);
        zeigeToast('🔕 Benachrichtigungen aus');
      } else {
        const token = await requestNotificationPermission(uid);
        if (token) zeigeToast('🔔 Benachrichtigungen an');
        else zeigeToast('Nicht möglich – bitte Benachrichtigungen im Browser erlauben', true);
      }
    } catch (error) {
      zeigeToast('Fehler: ' + error.message, true);
    } finally {
      setBusy(null);
    }
  };

  const handleProfil = async () => {
    const name = displayName.trim();
    if (!name) { zeigeToast('Bitte einen Namen eingeben', true); return; }
    setBusy('profil');
    try {
      const daten = { displayName: name };
      if (favoritenTeam) {
        daten.favoritenTeam = favoritenTeam;
        daten.favoritenTeamName = NFL_TEAMS[favoritenTeam]?.name || '';
      }
      await setDoc(doc(db, 'users', uid), daten, { merge: true });
      zeigeToast('✓ Profil gespeichert');
    } catch (error) {
      zeigeToast('Speichern fehlgeschlagen: ' + error.message, true);
    } finally {
      setBusy(null);
    }
  };

  const handlePasswort = async () => {
    if (!altesPasswort) { zeigeToast('Bitte das aktuelle Passwort eingeben', true); return; }
    if (neuesPasswort.length < 6) { zeigeToast('Neues Passwort: mindestens 6 Zeichen', true); return; }
    if (neuesPasswort !== bestaetigung) { zeigeToast('Die Passwörter stimmen nicht überein', true); return; }
    setBusy('passwort');
    try {
      await reauthenticateWithCredential(currentUser, EmailAuthProvider.credential(currentUser.email, altesPasswort));
      await updatePassword(currentUser, neuesPasswort);
      setAltesPasswort(''); setNeuesPasswort(''); setBestaetigung('');
      zeigeToast('✓ Passwort geändert');
    } catch (error) {
      const falsch = error.code === 'auth/wrong-password' || error.code === 'auth/invalid-credential';
      zeigeToast(falsch ? 'Das aktuelle Passwort ist falsch' : 'Fehler: ' + error.message, true);
    } finally {
      setBusy(null);
    }
  };

  const handleLoeschen = async () => {
    if (!loeschenPasswort) { zeigeToast('Bitte dein Passwort eingeben', true); return; }
    setBusy('loeschen');
    try {
      await reauthenticateWithCredential(currentUser, EmailAuthProvider.credential(currentUser.email, loeschenPasswort));
      await setDoc(doc(db, 'users', uid), {
        deleted: true,
        deletedAt: new Date().toISOString(),
        email: currentUser.email
      }, { merge: true });
      await deleteUser(currentUser);
      navigate('/');
    } catch (error) {
      const falsch = error.code === 'auth/wrong-password' || error.code === 'auth/invalid-credential';
      zeigeToast(falsch ? 'Falsches Passwort' : 'Fehler: ' + error.message, true);
      setBusy(null);
    }
  };

  return (
    <div className="seite einstellungen">
      <h1>⚙️ Einstellungen</h1>
      <p className="seite__untertitel">Angemeldet als {currentUser?.email}</p>

      {/* ---------- Push ---------- */}
      <div className="karte">
        <div className="schalter-zeile">
          <div>
            <h2>🔔 Push-Benachrichtigungen</h2>
            <p className="karte__hinweis" style={{ margin: 0 }}>
              Erinnerung 24 h und 6 h vor dem nächsten Kickoff, wenn du noch nicht getippt hast.
              {!pushMoeglich && ' Dein Browser unterstützt das nicht – auf dem iPhone die Seite als App installieren (siehe FAQ).'}
            </p>
          </div>
          <button
            type="button"
            className={`schalter-button${notificationsEnabled ? ' ist-an' : ''}`}
            onClick={handlePush}
            disabled={!pushMoeglich || busy === 'push'}
            aria-pressed={notificationsEnabled}
          >
            {busy === 'push' ? '⏳' : notificationsEnabled ? 'AN' : 'AUS'}
          </button>
        </div>
      </div>

      {/* ---------- Dark Mode ---------- */}
      <div className="karte">
        <div className="schalter-zeile">
          <div>
            <h2>🌙 Dark Mode</h2>
            <p className="karte__hinweis" style={{ margin: 0 }}>Dunkles Design, gespeichert auf diesem Gerät.</p>
          </div>
          <button type="button" className={`schalter-button${darkMode ? ' ist-an' : ''}`} onClick={toggleDarkMode} aria-pressed={darkMode}>
            {darkMode ? 'AN' : 'AUS'}
          </button>
        </div>
      </div>

      {/* ---------- Profil ---------- */}
      <div className="karte">
        <h2>👤 Profil</h2>
        <label className="feld">
          <span className="feld__label">Anzeigename</span>
          <input type="text" className="feld__input" value={displayName} onChange={e => setDisplayName(e.target.value)} placeholder="Dein Name" maxLength={40} />
        </label>
        <div className="feld">
          <TeamAuswahl label="⭐ Lieblingsteam (optional)" value={favoritenTeam} teams={Object.keys(NFL_TEAMS)} onChange={setFavoritenTeam} platzhalter="Kein Lieblingsteam" />
          <p className="karte__hinweis" style={{ margin: '6px 0 0' }}>
            Hebt die Spiele deines Teams hervor und schaltet im Ranking einen eigenen Reiter frei.
          </p>
        </div>
        <button type="button" className="aktion" onClick={handleProfil} disabled={busy === 'profil' || !profilGeaendert}>
          {busy === 'profil' ? '⏳ Speichere…' : '💾 Profil speichern'}
        </button>
      </div>

      {/* ---------- Passwort ---------- */}
      <div className="karte">
        <h2>🔒 Passwort ändern</h2>
        <label className="feld">
          <span className="feld__label">Aktuelles Passwort</span>
          <input type={zeigePasswort ? 'text' : 'password'} className="feld__input" value={altesPasswort} onChange={e => setAltesPasswort(e.target.value)} autoComplete="current-password" />
        </label>
        <label className="feld">
          <span className="feld__label">Neues Passwort (mindestens 6 Zeichen)</span>
          <input type={zeigePasswort ? 'text' : 'password'} className="feld__input" value={neuesPasswort} onChange={e => setNeuesPasswort(e.target.value)} autoComplete="new-password" />
        </label>
        <label className="feld">
          <span className="feld__label">Neues Passwort wiederholen</span>
          <input type={zeigePasswort ? 'text' : 'password'} className="feld__input" value={bestaetigung} onChange={e => setBestaetigung(e.target.value)} autoComplete="new-password" />
        </label>
        <div className="feld-zeile">
          <label className="schalter">
            <input type="checkbox" checked={zeigePasswort} onChange={e => setZeigePasswort(e.target.checked)} /> Passwörter anzeigen
          </label>
          <button type="button" className="aktion" onClick={handlePasswort} disabled={busy === 'passwort' || !altesPasswort || !neuesPasswort}>
            {busy === 'passwort' ? '⏳ Ändere…' : '🔐 Passwort ändern'}
          </button>
        </div>
      </div>

      {/* ---------- Account löschen ---------- */}
      <div className="karte karte--gefahr">
        <h2>🗑️ Account löschen</h2>
        <p className="karte__hinweis">
          Dein Login wird gelöscht und dein Profil als gelöscht markiert. Das lässt sich nicht rückgängig machen.
        </p>
        {!loeschenOffen ? (
          <button type="button" className="aktion aktion--gefahr" onClick={() => setLoeschenOffen(true)}>Account löschen…</button>
        ) : (
          <div className="loeschen">
            <label className="feld">
              <span className="feld__label">Zur Bestätigung dein Passwort</span>
              <input type="password" className="feld__input" value={loeschenPasswort} onChange={e => setLoeschenPasswort(e.target.value)} autoComplete="current-password" />
            </label>
            <div className="feld-zeile">
              <button type="button" className="aktion" onClick={() => { setLoeschenOffen(false); setLoeschenPasswort(''); }} disabled={busy === 'loeschen'}>Abbrechen</button>
              <button type="button" className="aktion aktion--gefahr" onClick={handleLoeschen} disabled={busy === 'loeschen' || !loeschenPasswort}>
                {busy === 'loeschen' ? '⏳ Lösche…' : 'Endgültig löschen'}
              </button>
            </div>
          </div>
        )}
      </div>

      {toast && <div className={`tipp-toast${toast.fehler ? ' tipp-toast--fehler' : ''}`} role="status">{toast.text}</div>}
    </div>
  );
}

export default Einstellungen;
